import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { type SentimentAudience, type SentimentCounts, api } from '../../lib/api'
import { faDateTime, faNum } from './labels'

const PERIODS = [7, 30, 90] as const

const AUDIENCES: { key: SentimentAudience; labelFa: string }[] = [
  { key: 'charity', labelFa: 'مؤسسه‌ها' },
  { key: 'staff', labelFa: 'کارشناسان افرا' },
]

/**
 * How the people talking to the agent feel about it.
 *
 * Every message an organization sends is read by Jev for its tone and for
 * whether the agent itself upset them. The shares sit beside the number of
 * messages they rest on, and the upsetting ones are listed in full with what
 * the agent had just said — a complaint without the line that caused it is
 * not something anyone can fix.
 */
export function SentimentTab() {
  const [days, setDays] = useState<(typeof PERIODS)[number]>(30)
  /* Charities and staff are never counted together: staff testing the agent in
     their personal spaces would otherwise decide how charities seem to feel. */
  const [audience, setAudience] = useState<SentimentAudience>('charity')
  const report = useQuery({
    queryKey: ['admin-sentiment', days, audience],
    queryFn: () => api.adminSentiment(days, audience),
  })
  const data = report.data

  return (
    <div className="stack">
      <div className="card">
        <h2>رضایت کاربران از گفت‌وگو با افرا</h2>
        <p className="meta">
          هر پیامی که مؤسسه‌ها برای افرا می‌فرستند را Jev می‌خواند: لحنش مثبت است، خنثی یا منفی، و آیا
          خود افرا یا گفت‌وگو ناراحتشان کرده. این خوانش هیچ‌وقت به خود افرا نشان داده نمی‌شود.
        </p>

        <div className="row" style={{ marginBlockStart: 'var(--space-4)' }}>
          {AUDIENCES.map((a) => (
            <button
              key={a.key}
              type="button"
              className={`btn btn--sm ${audience === a.key ? '' : 'btn--quiet'}`}
              aria-pressed={audience === a.key}
              onClick={() => setAudience(a.key)}
            >
              {a.labelFa}
            </button>
          ))}
        </div>

        <div className="row" style={{ marginBlock: 'var(--space-3) var(--space-4)' }}>
          {PERIODS.map((p) => (
            <button
              key={p}
              type="button"
              className={`btn btn--sm ${days === p ? '' : 'btn--quiet'}`}
              aria-pressed={days === p}
              onClick={() => setDays(p)}
            >
              {faNum(p)} روز اخیر
            </button>
          ))}
        </div>

        {report.isLoading && <p className="muted">در حال بارگذاری…</p>}
        {report.isError && <p className="notice notice--error">گزارش بارگذاری نشد.</p>}
        {data && <Overall counts={data.overall} />}
        {data && data.otherAudienceMessages > 0 && (
          <p className="meta" style={{ marginTop: 'var(--space-2)' }}>
            <span className="num">{faNum(data.otherAudienceMessages)}</span>{' '}
            {audience === 'charity'
              ? 'پیام از کارشناسان افرا در فضای شخصی‌شان در این بازه هست که اینجا شمرده نشده.'
              : 'پیام از مؤسسه‌ها در این بازه هست که اینجا شمرده نشده.'}
          </p>
        )}
      </div>

      {data && data.orgs.length > 0 && (
        <div className="card">
          <h3>به تفکیک مؤسسه</h3>
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>مؤسسه</th>
                  <th>پیام‌ها</th>
                  <th>مثبت</th>
                  <th>منفی</th>
                  <th>ناراحت از افرا</th>
                </tr>
              </thead>
              <tbody>
                {data.orgs.map((o) => (
                  <tr key={o.orgId}>
                    <td>{o.orgName}</td>
                    <td className="num">{faNum(o.total)}</td>
                    <td className="num">{share(o.positive, o.rated)}</td>
                    <td className="num">{share(o.negative, o.rated)}</td>
                    <td>
                      {o.upset > 0 ? (
                        <span className="badge badge--bad">
                          <span className="num">{faNum(o.upset)}</span> پیام
                        </span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data && (
        <div className="card">
          <h3>پیام‌هایی که کاربر را ناراحت کرد</h3>
          {data.upset.length === 0 ? (
            <p className="muted">در این بازه پیامی نبود که نشان بدهد افرا کسی را ناراحت کرده.</p>
          ) : (
            <div className="stack">
              {data.upset.map((m) => (
                <div key={m.messageId} className="dimension" style={{ gridTemplateColumns: '1fr' }}>
                  <div className="meta">
                    {m.orgName} · {faDateTime(m.at)} · احتمال ناراحتی{' '}
                    <UpsetGauge probability={m.probability} />{' '}
                    <span className="num">{faNum(Math.round(m.probability * 100))}٪</span>{' '}
                    <span className="badge badge--generated" title={m.decidedBy}>
                      خوانش مدل
                    </span>
                  </div>
                  {m.previousAgentFa && (
                    <div className="exchange">
                      <div className="meta">افرا گفته بود:</div>
                      <div>{m.previousAgentFa}</div>
                    </div>
                  )}
                  <div className="exchange">
                    <div className="meta">جواب کاربر:</div>
                    <div>{m.textFa}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Overall({ counts }: { counts: SentimentCounts }) {
  if (counts.total === 0) return <p className="muted">در این بازه پیامی فرستاده نشده است.</p>

  const unrated = counts.total - counts.rated
  return (
    <>
      <div className="stats">
        <Stat label="مثبت" value={share(counts.positive, counts.rated)} basis={counts.rated} />
        <Stat label="خنثی" value={share(counts.neutral, counts.rated)} basis={counts.rated} />
        <Stat label="منفی" value={share(counts.negative, counts.rated)} basis={counts.rated} />
        <Stat label="ناراحت از افرا" value={share(counts.upset, counts.rated)} basis={counts.rated} />
      </div>
      <p className="meta" style={{ marginTop: 'var(--space-3)' }}>
        «منفی» یعنی لحن پیام منفی بوده، که می‌تواند درباره وضعیت خود مؤسسه باشد. «ناراحت از افرا» یعنی
        خود افرا یا گفت‌وگو کاربر را ناراحت کرده.
        {unrated > 0 && (
          <>
            {' '}
            <span className="num">{faNum(unrated)}</span> پیام هنوز خوانده نشده.
          </>
        )}
      </p>
    </>
  )
}

function Stat({ label, value, basis }: { label: string; value: string; basis: number }) {
  return (
    <div className="stat">
      <div className="stat__label">{label}</div>
      <div className="stat__value num">{value}</div>
      <div className="stat__basis">
        از <span className="num">{faNum(basis)}</span> پیام
      </div>
    </div>
  )
}

/** Above this the agent almost certainly upset them: the gauge turns red. */
const UPSET_DANGER = 0.7
/** A message is listed as upset from 0.5; between the two it is amber. */
const UPSET_WARN = 0.5

/**
 * How sure Jev is that the agent upset someone, as a small meter. The
 * percentage beside it carries the value; the colour only says how worried to be.
 */
function UpsetGauge({ probability }: { probability: number }) {
  const p = Math.max(0, Math.min(1, probability))
  const level = p > UPSET_DANGER ? 'gauge--bad' : p >= UPSET_WARN ? 'gauge--warn' : ''
  return (
    <span
      className={`gauge ${level}`}
      role="meter"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(p * 100)}
      aria-label="احتمال ناراحتی"
    >
      <span className="gauge__fill" style={{ inlineSize: `${p * 100}%` }} />
    </span>
  )
}

/** A share of what was actually read, never of what was sent: an unread message is not a neutral one. */
function share(part: number, of: number): string {
  if (of === 0) return '—'
  return `${faNum(Math.round((part / of) * 100))}٪`
}
